import { useState, useCallback, useMemo, useRef, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { RefreshCw, AlertTriangle } from 'lucide-react';
import { PageHeader } from '@/components/layout';
import { Button, Card, CardContent, TableSkeleton } from '@/components/ui';
import { CursorPagination } from '@/components/ui/cursor-pagination';
import { ApiErrorDisplay } from '@/components/features/error';
import { SubscriptionFilters } from '@/components/features/subscriptions/subscription-filters';
import { SubscriptionTable, type SubscriptionSortColumn } from '@/components/features/subscriptions/subscription-table';
import { TerminateConfirmation } from '@/components/features/subscriptions/terminate-confirmation';
import { useAdminSubscriptions, useTerminateSubscription } from '@/hooks/use-admin-subscriptions';
import { useSubscriptionFilterParams } from '@/hooks/use-subscription-filter-params';
import { useAuth } from '@/hooks/use-auth';
import { useMerchantScope } from '@/hooks/use-merchant-scope';
import { useSortState } from '@/hooks/use-sort-state';
import { ROLE_GROUPS } from '@/types/auth';
import { sortItems } from '@/lib/utils/sort';
import { filterByDateRange } from '@/lib/utils/client-filters';
import type { ListAdminSubscriptionsParams } from '@/types/admin-subscription';

export function SubscriptionsPage() {
  const { t } = useTranslation('subscriptions');
  const location = useLocation();
  const { hasRole } = useAuth();
  const canTerminate = hasRole(ROLE_GROUPS.SUB_CANCEL);
  // Spec 0220: Merchant-Spalte nur fuer Platform-Rollen.
  const { showMerchant } = useMerchantScope();

  // Spec 410: Filter-State liegt in der URL (mirror der TX-Liste #300/#380) statt im
  // bespoke localStorage-Snapshot. Service/Operator/Status sind Mehrfach+Ausschluss,
  // subId/renewals sind abgeleitete Achsen; das BFF scannt pagination-sicher.
  const {
    filters,
    dateFrom,
    dateTo,
    hasActiveFilters,
    setFilters,
    setDateRange,
    reset,
  } = useSubscriptionFilterParams();

  const [cursorStack, setCursorStack] = useState<string[]>([]);
  const currentCursor = cursorStack.length > 0 ? cursorStack[cursorStack.length - 1] : undefined;

  // Spec 410: der Pager wird bei JEDER Server-Filter-Aenderung zurueckgesetzt — auch bei
  // externen URL-Mutationen (Back/Forward, Deep-Link). Sonst bliebe ein Cursor aus einem
  // anderen Filtersatz aktiv. Der lokale Reset in den Handlern bleibt zusaetzlich (sofortig).
  const serverFiltersKey = JSON.stringify({
    merchantId: filters.merchantId,
    service: filters.service,
    statuses: filters.statuses,
    operator: filters.operator,
    subId: filters.subId,
    renewals: filters.renewals,
    limit: filters.limit,
  });
  const prevFiltersKeyRef = useRef(serverFiltersKey);
  useEffect(() => {
    if (prevFiltersKeyRef.current !== serverFiltersKey) {
      prevFiltersKeyRef.current = serverFiltersKey;
      setCursorStack([]);
    }
  }, [serverFiltersKey]);

  // Sortierung (client-seitig, Default: Datum desc)
  const { sort, toggleSort } = useSortState<SubscriptionSortColumn>('createdAt', 'desc');

  // Spec 481: die TX-Such-Kaskade landet hier mit expandSubscriptionId im
  // Navigations-State — EINMALIGE Mount-Initialisierung (useState-Initializer),
  // kein Re-Expansion-Trigger bei spaeteren Renders; Zuklappen bleibt
  // zugeklappt. Danach besitzt die Seite den State wie bisher.
  const [expandedId, setExpandedId] = useState<string | null>(
    () =>
      (location.state as { expandSubscriptionId?: string } | null)?.expandSubscriptionId ?? null,
  );
  // Den One-Shot-State nach dem Konsum aus dem History-Eintrag entfernen —
  // sonst wuerde ein Back/Forward-Restore desselben Eintrags die Zeile erneut
  // aufklappen, obwohl der Nutzer sie geschlossen hatte (Spec 481 / Codex-
  // Review Phase 2). replace haelt URL (subId) und History-Tiefe unveraendert.
  const navigate = useNavigate();
  useEffect(() => {
    if ((location.state as { expandSubscriptionId?: string } | null)?.expandSubscriptionId) {
      navigate(location.pathname + location.search, { replace: true, state: null });
    }
    // Bewusst nur beim Mount — der Initializer hat den State bereits konsumiert.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const [terminatingId, setTerminatingId] = useState<string | null>(null);

  const { data, isLoading, error, refetch } = useAdminSubscriptions({
    ...filters,
    cursor: currentCursor,
  });

  const terminateMutation = useTerminateSubscription();

  // Server-Filter Handler (resettet Cursor)
  const handleFiltersChange = useCallback((newFilters: ListAdminSubscriptionsParams) => {
    setFilters(newFilters);
    setCursorStack([]);
  }, [setFilters]);

  const handleReset = useCallback(() => {
    reset();
    setCursorStack([]);
  }, [reset]);

  const handleNextPage = useCallback((cursor: string) => {
    setCursorStack((prev) => [...prev, cursor]);
  }, []);

  const handlePreviousPage = useCallback(() => {
    setCursorStack((prev) => prev.slice(0, -1));
  }, []);

  const handleToggleExpand = useCallback((subscriptionId: string) => {
    setExpandedId((prev) => (prev === subscriptionId ? null : subscriptionId));
  }, []);

  const handleTerminateClick = useCallback((subscriptionId: string) => {
    setTerminatingId(subscriptionId);
  }, []);

  const handleTerminateConfirm = useCallback((reason?: string) => {
    if (!terminatingId) return;
    terminateMutation.mutate(
      { subscriptionId: terminatingId, reason },
      {
        onSuccess: () => {
          setTerminatingId(null);
          setExpandedId(null);
        },
      }
    );
  }, [terminatingId, terminateMutation]);

  // Pipeline: API-Daten → Client-Datumsfilter → Sort. Spec 410: der Operator ist jetzt eine
  // BFF-Scan-Achse (kein page-lokaler Textfilter mehr); Datum bleibt page-lokal (wie TX).
  const { filteredItems, totalCount } = useMemo(() => {
    if (!data?.data) return { filteredItems: [], totalCount: 0 };
    const items = data.data;
    const total = items.length;

    let result = filterByDateRange(items, 'createdAt', dateFrom, dateTo);
    result = sortItems(result, sort.column, sort.direction);

    return { filteredItems: result, totalCount: total };
  }, [data?.data, dateFrom, dateTo, sort.column, sort.direction]);

  const hasActiveClientFilters = dateFrom || dateTo;
  const showFilterHint = hasActiveClientFilters && filteredItems.length < totalCount;

  return (
    <div>
      <PageHeader
        title={t('list.title')}
        description={t('list.description')}
        actions={
          <Button variant="outline" size="sm" onClick={() => refetch()}>
            <RefreshCw className="mr-2 h-4 w-4" />
            {t('common:button.refresh')}
          </Button>
        }
      />

      <SubscriptionFilters
        filters={filters}
        onFiltersChange={handleFiltersChange}
        dateFrom={dateFrom}
        dateTo={dateTo}
        onDateRangeChange={setDateRange}
        canReset={hasActiveFilters}
        onReset={handleReset}
      />

      {/* Spec 410: BFF-Scan-Degradation — `truncated` (Seitenschranke erreicht) bzw.
          `unavailable` (Scan fehlgeschlagen). Getrennt vom page-lokalen filterHint (Datum). */}
      {!isLoading && !error && data && (data.truncated || data.unavailable) && (
        <div
          role="alert"
          className="mb-4 flex items-start gap-2 rounded-md border border-amber-300 bg-amber-50 p-3 text-sm text-amber-900"
        >
          <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" aria-hidden="true" />
          <span>
            {data.unavailable
              ? t('list.banner.unavailable', 'Filtern momentan nicht verfügbar. Bitte erneut versuchen.')
              : t('list.banner.truncated', 'Zu viele Treffer — Teilergebnis. Filter weiter eingrenzen.')}
          </span>
        </div>
      )}

      {isLoading && (
        <Card>
          <CardContent className="py-6">
            <TableSkeleton rows={5} cols={8} />
          </CardContent>
        </Card>
      )}

      {error && <ApiErrorDisplay error={error} onRetry={() => refetch()} />}

      {!isLoading && !error && data && (
        <Card>
          {showFilterHint && (
            <div className="border-b px-4 py-2 text-sm text-muted-foreground">
              {t('list.filterHint', {
                shown: filteredItems.length,
                total: totalCount,
                defaultValue: 'Zeige {{shown}} von {{total}} Einträgen auf dieser Seite',
              })}
            </div>
          )}
          <SubscriptionTable
            subscriptions={filteredItems}
            expandedId={expandedId}
            onToggleExpand={handleToggleExpand}
            onTerminate={canTerminate ? handleTerminateClick : undefined}
            isTerminating={terminateMutation.isPending}
            sort={sort}
            onToggleSort={toggleSort}
            showMerchant={showMerchant}
          />
          <CursorPagination
            hasMore={data.pagination.hasMore}
            nextCursor={data.pagination.nextCursor}
            onNext={handleNextPage}
            onPrevious={handlePreviousPage}
            hasPrevious={cursorStack.length > 0}
            isLoading={isLoading}
          />
        </Card>
      )}

      {terminatingId && (
        <TerminateConfirmation
          subscriptionId={terminatingId}
          open={!!terminatingId}
          onOpenChange={(open) => { if (!open) setTerminatingId(null); }}
          onConfirm={handleTerminateConfirm}
          isPending={terminateMutation.isPending}
        />
      )}
    </div>
  );
}
